import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import AppContext from "../../../context/Appcontext";
import "./../../../assets/css/student/Login/login.css";
import loginImage from "../../../assets/images/myLogin.png";

// ─── SVG Icons ────────────────────────────────────────────────
const IconEnvelope = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 01-2.06 0L2 7" />
  </svg>
);

const IconLock = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

const IconEyeOpen = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const IconEyeClosed = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);
// ──────────────────────────────────────────────────────────────

const Login = () => {
  const navigate = useNavigate();
  const { baseUrl } = useContext(AppContext);

  // ── Page loader state ──
  const [pageLoading, setPageLoading] = useState(true);

  // ── Form fields ──
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  // ── UI states ──
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setPageLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  // ── Login submit ──
  const handleLogin = (event) => {
    event.preventDefault();
    setErrorMessage("");

    setIsLoading(true);

    axios
      .post(`${baseUrl}/student/login`, {
        email,
        password,
      })
      .then((response) => {
        setIsLoading(false);

        const student = response.data.student;

        // Save student data in localStorage
        localStorage.setItem("studentData", JSON.stringify(student));
        localStorage.setItem("firstName", student?.firstName || "");
        localStorage.setItem("lastName", student?.lastName || "");
        localStorage.setItem("email", email);
        localStorage.setItem("role", "student");

        const userName = student?.firstName || email.split("@")[0];

        Swal.fire({
          icon: "success",
          title: `Welcome back, ${userName}!`,
          text: "Login successful",
          showConfirmButton: false,
          timer: 1500,
        }).then(() => navigate("/student/dashboard"));
      })
      .catch((error) => {
        setIsLoading(false);
        const data = error.response?.data;

        // Agar backend se multiple errors aayein
        if (data?.errors) {
          setErrorMessage(Object.values(data.errors).flat().join(", "));
        } else {
          setErrorMessage(
            data?.message || "Invalid credentials. Please try again.",
          );
        }
      });
  };

  // ─── Page loader ───
  if (pageLoading) {
    return (
      <div className="login-loader">
        <div className="login-spinner" />
      </div>
    );
  }

  // ─── Main UI ───
  return (
    <div className="login-page">
      {/* Left — Form */}
      <div className="login-left">
        <div className="login-card">
          <div className="login-card-header">
            <h1 className="login-title">Welcome back</h1>
            <h2 className="login-subtitle">Login</h2>
            <p className="login-desc">Enter your account details</p>
          </div>

          <form onSubmit={handleLogin} noValidate>
            {/* Email */}
            <div className="login-field">
              <label className="login-label" htmlFor="loginEmail">
                Email
              </label>
              <div className="login-input-wrap">
                <span className="login-input-icon" aria-hidden="true">
                  <IconEnvelope />
                </span>
                <input
                  className="login-input"
                  id="loginEmail"
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password */}
            <div className="login-field">
              <label className="login-label" htmlFor="loginPassword">
                Password
              </label>
              <div className="login-input-wrap">
                <span className="login-input-icon" aria-hidden="true">
                  <IconLock />
                </span>
                <input
                  className="login-input"
                  id="loginPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="login-eye"
                  onClick={() => setShowPassword((previous) => !previous)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <IconEyeClosed /> : <IconEyeOpen />}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="login-meta">
              <label className="login-remember">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span className="login-check-box" aria-hidden="true" />
                Remember me
              </label>
              <button
                type="button"
                className="login-forgot"
                onClick={() => navigate("/forgot/password")}
              >
                Forgot password?
              </button>
            </div>

            {/* Error Message */}
            {errorMessage && (
              <div className="login-error" role="alert">
                {errorMessage}
              </div>
            )}

            {/* Submit Button */}
            <button type="submit" className="login-btn" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="login-btn-spinner" aria-hidden="true" />{" "}
                  Logging in…
                </>
              ) : (
                "Login"
              )}
            </button>
          </form>

          <p className="login-signup-row">
            Don't have an account?
            <button
              type="button"
              className="login-signup-link"
              onClick={() => navigate("/signup")}
            >
              Sign up
            </button>
          </p>
        </div>
      </div>

      {/* Right — Image */}
      <div className="login-right">
        <div className="login-illus-wrap">
          <img
            src={loginImage}
            alt="Students learning"
            className="login-illus"
          />
        </div>
        <div className="login-right-inner">
          <div className="login-right-text">
            <p className="login-rt-top">Welcome to</p>
            <h2 className="login-rt-title">student portal</h2>
            <p className="login-rt-sub">Login to access your account</p>
          </div>
        </div>
        <div>
          <button className="login-back-btn" onClick={() => navigate("/")}>
            Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
